import React, { useRef, useState } from 'react';
import './Support&Help.css';
import { axiosInstance } from '../../../utils/tokenUtils';
import { toast } from 'react-hot-toast';
import { MessageQuestion } from 'iconsax-react';

const SupportHelpModal = ({ openSupportHelpModal, setOpenSupportHelpModal }) => {
  const [issueCategories, setIssueCategories] = useState([]);
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const MAX_FILE_SIZE_MB = 5;
  const MAX_DESCRIPTION = 500;

  const issueOptions = [
    { value: 'app-loading', label: 'The App is not loading' },
    { value: 'session-booking', label: 'Session booking not working' },
    { value: 'notifications', label: 'Not receiving notifications' },
    { value: 'other', label: 'Others' },
  ];

  const backendCategoryMap = {
    'app-loading': 'app_not_loading',
    'session-booking': 'session_booking_not_working',
    notifications: 'not_receiving_notifications',
    other: 'others',
  };

  const isSubmitDisabled =
    submitting || issueCategories.length === 0 || description.trim().length < 10;

  if (!openSupportHelpModal) return null;

  const close = () => setOpenSupportHelpModal(false);

  const toggleCategory = value => {
    setIssueCategories(prev =>
      prev.includes(value) ? prev.filter(cat => cat !== value) : [...prev, value]
    );
    setErrors(prev => ({ ...prev, category: '' }));
  };

  const isFileTypeAllowed = file => {
    const allowedPrefixes = ['image/', 'application/pdf'];
    return allowedPrefixes.some(prefix => file.type?.startsWith(prefix));
  };

  const validateSingleFile = file => {
    if (!file) return { ok: true };
    const sizeMb = file.size / (1024 * 1024);
    if (!isFileTypeAllowed(file)) {
      return { ok: false, msg: `${file.name}: unsupported type` };
    }
    if (sizeMb > MAX_FILE_SIZE_MB) {
      return { ok: false, msg: `${file.name}: exceeds ${MAX_FILE_SIZE_MB}MB` };
    }
    return { ok: true };
  };

  const handleFileChange = e => {
    const selected = Array.from(e.target.files || []);
    const next = selected[0];
    const result = validateSingleFile(next);
    if (!result.ok) {
      setErrors(prev => ({ ...prev, files: result.msg }));
      return;
    }
    setErrors(prev => ({ ...prev, files: '' }));
    setFiles(next ? [next] : []);
  };

  const handleDrop = e => {
    e.preventDefault();
    setDragActive(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    const next = dropped[0];
    const result = validateSingleFile(next);
    if (!result.ok) {
      setErrors(prev => ({ ...prev, files: result.msg }));
      return;
    }
    setErrors(prev => ({ ...prev, files: '' }));
    setFiles(next ? [next] : []);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const trimmed = description.trim();
    const submitErrors = {};

    if (issueCategories.length === 0)
      submitErrors.category = 'Please select at least one issue category.';
    if (trimmed.length < 10)
      submitErrors.description =
        'Please provide a brief description (minimum 10 characters required).';

    if (Object.keys(submitErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...submitErrors }));
      return;
    }

    setSubmitting(true);
    try {
      const mappedCategories = issueCategories.map(c => backendCategoryMap[c] || c);

      // Step 1: Create support request
      const createRes = await axiosInstance.post('/common/support_and_help', {
        issue_category: mappedCategories,
        description: trimmed,
        file: '',
      });

      // Extract the support ID from the response
      const supportId = createRes.data?.id;

      if (!supportId) {
        throw new Error('Failed to create support request: No ID received');
      }

      // Step 2: If there is a file, upload it using returned id
      if (files.length === 1 && files[0]) {
        const formData = new FormData();
        formData.append('file', files[0]);

        await axiosInstance.post(`/common/support_and_help/${supportId}/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      // Success - only close modal after both API calls succeed
      setSubmitting(false);
      toast.success('Support request submitted successfully!');
      close();

      // Reset local state after closing
      setTimeout(() => {
        setIssueCategories([]);
        setDescription('');
        setFiles([]);
        setErrors({});
        setDropdownOpen(false);
      }, 0);
    } catch (err) {
      setSubmitting(false);
      // Convert error object to string to avoid React rendering objects directly
      let errorMessage = 'Something went wrong. Please try again.';

      if (err.response?.data?.detail) {
        // Handle array of error details
        if (Array.isArray(err.response.data.detail)) {
          errorMessage = err.response.data.detail.map(item => item.msg).join(', ');
        } else if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setErrors(prev => ({
        ...prev,
        submit: errorMessage,
      }));
    }
  };

  const getDisplayText = () => {
    if (issueCategories.length === 0) return 'Select your issue';
    if (issueCategories.length === 1) {
      return (
        issueOptions.find(opt => opt.value === issueCategories[0])?.label || 'Select your issue'
      );
    }
    return `${issueCategories.length} issues selected`;
  };

  return (
    <div className="shm-overlay" onClick={close}>
      <div className="shm-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <button className="shm-close" onClick={close} aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M6 6L18 18M6 18L18 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <form className="shm-form" onSubmit={handleSubmit}>
          <div className="shm-card">
            <div className="shm-head">
              <div className="shm-head-row">
                <div className="shm-head-icon" aria-hidden="true">
                  <MessageQuestion size="24" color="#0A5EF3" variant="Bold" />
                </div>
                <div>
                  <div className="shm-title">Support & Help</div>
                </div>
              </div>
            </div>

            <div className="shm-help-section">
              <div className="shm-help-title">Need Help? We're Here!</div>
              <div className="shm-help-subtitle">
                Facing technical issues? Let us know, and we'll get it fixed ASAP.
              </div>
            </div>

            <div className="shm-field">
              <label className="shm-label">Issue Category</label>
              <div className="shm-dropdown-wrapper">
                <div
                  className="shm-dropdown-trigger"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  aria-invalid={!!errors.category}
                >
                  <span className="shm-dropdown-text">{getDisplayText()}</span>
                  <span
                    className={`shm-dropdown-arrow ${dropdownOpen ? 'shm-dropdown-arrow--open' : ''}`}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <polyline
                        points="6,9 12,15 18,9"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </div>
                {dropdownOpen && (
                  <div className="shm-dropdown-menu">
                    {issueOptions.map(option => (
                      <label key={option.value} className="shm-dropdown-option">
                        <input
                          type="checkbox"
                          checked={issueCategories.includes(option.value)}
                          onChange={() => toggleCategory(option.value)}
                          className="shm-checkbox"
                        />
                        <span className="shm-checkbox-label">{option.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {errors.category && (
                <div className="shm-error" role="alert">
                  {errors.category}
                </div>
              )}
            </div>

            <div className="shm-field">
              <label className="shm-label">Describe the issue</label>
              <textarea
                className="shm-textarea"
                placeholder="Describe your issue in detail so we can help you better. (Minimum 10 characters required)"
                value={description}
                onChange={e => {
                  const next = e.target.value.slice(0, MAX_DESCRIPTION);
                  setDescription(next);
                  setErrors(prev => ({ ...prev, description: '' }));
                }}
                rows={4}
                aria-invalid={!!errors.description}
              />
              <div className="shm-row-between">
                <div className="shm-error" role="alert">
                  {errors.description}
                </div>
                <div className="shm-help-text">
                  {description.trim().length < 10 ? (
                    <span style={{ color: '#dc2626' }}>
                      {description.trim().length}/10 characters
                    </span>
                  ) : (
                    <span style={{ color: '#6b7280' }}>
                      {description.length}/{MAX_DESCRIPTION} characters
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="shm-field">
              <label className="shm-label">Upload File</label>
              <div
                className={`shm-upload ${dragActive ? 'shm-upload--active' : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={e => e.preventDefault()}
                onDragEnter={() => setDragActive(true)}
                onDragLeave={() => setDragActive(false)}
              >
                <div className="shm-upload-row">
                  <div className="shm-upload-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 16V4M12 4L8 8M12 4L16 8"
                        stroke="#0A5EF3"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M20 16V19C20 20.105 19.105 21 18 21H6C4.895 21 4 20.105 4 19V16"
                        stroke="#0A5EF3"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <div className="shm-upload-text">Click or drag a file here</div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  multiple={false}
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <div className="shm-help-text">
                  IMG and PDF files supported. Max file size: 5MB. Only one file allowed. Uploading
                  another will replace it.
                </div>
                {errors.files && (
                  <div className="shm-error" role="alert">
                    {errors.files}
                  </div>
                )}
                {files.length === 1 && (
                  <div className="shm-file-chips">
                    <div className="shm-chip" title={files[0].name}>
                      <span className="shm-chip-text">{files[0].name}</span>
                      <button
                        type="button"
                        className="shm-chip-remove"
                        aria-label={`Remove ${files[0].name}`}
                        onClick={e => {
                          e.stopPropagation();
                          setFiles([]);
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {errors.submit && (
              <div className="shm-error" role="alert">
                {errors.submit}
              </div>
            )}
          </div>

          <div className="shm-footer">
            <button
              type="button"
              className="shm-btn shm-btn--ghost"
              onClick={close}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="shm-btn shm-btn--primary" disabled={isSubmitDisabled}>
              {submitting ? (
                <span className="shm-inline-flex">
                  <span className="shm-spinner" aria-hidden="true" />
                  Submitting...
                </span>
              ) : (
                'Submit'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupportHelpModal;
